import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminApi, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, RotateCcw, ChevronDown, ChevronUp, ArrowUp, ArrowDown, Music, Film } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { LINK_META } from "@/data/platforms";
import { Field, Text, Area, Select, Toggle, Btn, ImageUploader, AudioUploader, VideoUploader, panel } from "@/admin/kit";
import { PreflightCard } from "@/admin/preflight";
import { MusicVideoBlock } from "@/admin/videoBlock";
import { LyricsStamper, ReadyToPost } from "@/admin/postKit";

// ------------ TrackEditor: rich per-track card (title, cover, audio, video URL/upload)
const TrackEditor = ({ track, index, total, onChange, onRemove, onMove }) => {
  const [expanded, setExpanded] = useState(false);
  const t = track;
  const patch = (obj) => onChange({ ...t, ...obj });
  const num = String(index + 1).padStart(2, "0");
  const hasAudio = Boolean(t.preview_url);
  const hasVideo = Boolean(t.video_url || t.video_media_id);
  const badges = [];
  if (hasAudio) badges.push({ label: "Audio", color: "var(--gold)" });
  if (hasVideo) badges.push({ label: "Video", color: "var(--rose)" });

  return (
    <div className="p-4" style={{ ...panel, marginBottom: 12 }} data-testid={`track-card-${index}`}>
      <div className="flex items-center gap-3">
        <span className="font-caps text-ash" style={{ fontSize: ".6rem", width: 24 }}>{num}</span>
        <div className="grid place-items-center" style={{ width: 44, height: 44, borderRadius: "var(--radius)", border: "var(--rule)", overflow: "hidden", background: "var(--ink)" }}>
          {t.artwork_url ? <img src={mediaUrl(t.artwork_url)} alt="" className="w-full h-full object-cover" /> : <Music size={16} color="var(--ash)" />}
        </div>
        <div className="flex-1 min-w-0">
          <Text value={t.title || ""} onChange={(e) => patch({ title: e.target.value })} placeholder="Track title" data-testid={`track-title-${index}`} />
          {badges.length > 0 && (
            <div className="flex gap-2 mt-1">
              {badges.map((b) => (
                <span key={b.label} className="font-caps" style={{ fontSize: ".5rem", color: b.color, border: `1px solid ${b.color}`, padding: ".15rem .4rem", borderRadius: "var(--radius)" }}>{b.label}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} title="Move up" className="text-ash disabled:opacity-30 hover:text-gold" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid={`track-up-${index}`}><ArrowUp size={13} /></button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} title="Move down" className="text-ash disabled:opacity-30 hover:text-gold" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid={`track-down-${index}`}><ArrowDown size={13} /></button>
        </div>
        <button type="button" onClick={() => setExpanded((v) => !v)} className="text-ash hover:text-gold" style={{ background: "none", border: "none", cursor: "pointer" }} data-testid={`track-toggle-${index}`} aria-label={expanded ? "Collapse" : "Expand"}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        <button type="button" onClick={onRemove} className="text-rose hover:opacity-80" style={{ background: "none", border: "none", cursor: "pointer" }} aria-label="Remove track" data-testid={`track-remove-${index}`}><Trash2 size={15} /></button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 grid gap-4" style={{ borderTop: "var(--rule)" }}>
          <ImageUploader
            value={t.artwork_url}
            onChange={(url) => patch({ artwork_url: url })}
            label="Track cover (recommended — shown when this track plays)"
          />

          <div>
            <p className="font-caps text-gold mb-2" style={{ fontSize: ".58rem" }}>Audio preview</p>
            <AudioUploader
              media={t.audio_media_id ? { id: t.audio_media_id, url: t.preview_url, preview_start: t.preview_start || 0, duration: t.duration, original_filename: t.audio_filename || "track.mp3", peaks: t.peaks } : null}
              onChange={(m) => patch({ audio_media_id: m.id, preview_url: m.url, preview_start: m.preview_start, duration: m.duration, peaks: m.peaks, full_peaks: m.full_peaks, audio_filename: m.original_filename })}
            />
          </div>

          <div>
            <p className="font-caps text-gold mb-2" style={{ fontSize: ".58rem" }}>Video (optional)</p>
            <Field label="Paste a YouTube or Vimeo URL" help="e.g., https://youtu.be/xxxx  or  https://vimeo.com/xxxx">
              <Text
                value={t.video_url || ""}
                onChange={(e) => patch({ video_url: e.target.value })}
                placeholder="https://"
                data-testid={`track-video-url-${index}`}
              />
            </Field>
            <p className="font-caps text-ash my-2" style={{ fontSize: ".55rem", textAlign: "center" }}>— or upload your own —</p>
            <VideoUploader
              media={t.video_media_id ? { id: t.video_media_id, url: t.video_file_url, poster_url: t.video_poster_url, original_filename: t.video_filename || "video.mp4", duration: t.video_duration || 0 } : null}
              onChange={(m) => patch({ video_media_id: m.id, video_file_url: m.url, video_poster_url: m.poster_url, video_filename: m.original_filename, video_duration: m.duration })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const emptyTrack = (nextIdx) => ({ number: nextIdx + 1, title: "", artwork_url: null, audio_media_id: null, preview_url: null, video_url: "", video_media_id: null });

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : "");

const Section = ({ title, children, onSave, saving, testid, id }) => (
  <div className="p-5 mb-5" style={{ ...panel, scrollMarginTop: 24 }} id={id}>
    <div className="flex items-center justify-between mb-4 gap-3">
      <p className="font-caps text-gold" style={{ fontSize: ".62rem" }}>{title}</p>
      {onSave && (
        <Btn onClick={onSave} disabled={saving} data-testid={testid || "section-save"} style={{ padding: ".45rem 1rem", fontSize: ".58rem" }}>
          {saving ? "Saving…" : "Save"}
        </Btn>
      )}
    </div>
    <div className="grid gap-4">{children}</div>
  </div>
);

// ------------ AudiencePicker: choose which subscriber interests receive the blast
const AudiencePicker = ({ releaseTitle, onCancel, onSend, sending, eyebrow = "Release-day blast" }) => {
  const INTERESTS = ["New music", "Merch", "Press & media"];
  const [selected, setSelected] = useState(["New music"]);
  const [counts, setCounts] = useState({ total: 0, by_interest: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminApi.get("/admin/subscribers/counts")
      .then(({ data }) => setCounts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggle = (i) => setSelected((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);
  const selectAll = () => setSelected(INTERESTS);
  const reach = selected.reduce((sum, i) => sum + (counts.by_interest[i] || 0), 0);
  const disabled = sending || reach === 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,.72)", backdropFilter: "blur(6px)" }}
         data-testid="audience-picker">
      <div className="p-6 max-w-[480px] w-full" style={{ ...panel, background: "var(--ink-raised)" }}>
        <p className="font-caps text-gold mb-1" style={{ fontSize: ".6rem" }}>{eyebrow}</p>
        <h2 className="font-display mb-4" style={{ fontSize: "1.4rem", color: "var(--parchment)" }}>
          Who should hear about {releaseTitle ? `"${releaseTitle}"` : "this release"}?
        </h2>
        <p className="mb-4" style={{ color: "var(--ash)", fontSize: ".78rem" }}>
          Only subscribers who picked one of these interests will get the email.
        </p>

        <div className="grid gap-2 mb-5">
          {INTERESTS.map((i) => {
            const c = counts.by_interest[i] || 0;
            const on = selected.includes(i);
            return (
              <label key={i} data-testid={`audience-${i.replace(/\s+/g, "-").toLowerCase()}`}
                     className="flex items-center gap-3 px-3 py-2.5 cursor-pointer"
                     style={{ border: on ? "1px solid var(--gold)" : "var(--rule)", borderRadius: "var(--radius)" }}>
                <input type="checkbox" checked={on} onChange={() => toggle(i)} disabled={sending} />
                <span style={{ color: "var(--parchment)", flex: 1 }}>{i}</span>
                <span className="font-caps text-gold" style={{ fontSize: ".62rem" }}>
                  {loading ? "…" : `${c} subscriber${c === 1 ? "" : "s"}`}
                </span>
              </label>
            );
          })}
        </div>

        <div className="flex items-center justify-between mb-4">
          <button onClick={selectAll} disabled={sending}
                  className="font-caps"
                  style={{ fontSize: ".6rem", color: "var(--ash)", background: "none", border: "none", cursor: "pointer" }}
                  data-testid="audience-select-all">
            Everyone ({counts.total})
          </button>
          <span className="font-caps" style={{ fontSize: ".7rem", color: "var(--parchment)" }} data-testid="audience-reach">
            Reaches {reach} subscriber{reach === 1 ? "" : "s"}
          </span>
        </div>

        <div className="flex gap-3 justify-end">
          <Btn variant="ghost" onClick={onCancel} disabled={sending} data-testid="audience-cancel">Cancel</Btn>
          <Btn onClick={() => onSend(selected.length === INTERESTS.length ? [] : selected)}
               disabled={disabled} data-testid="audience-send">
            {sending ? "Sending…" : `Send to ${reach}`}
          </Btn>
        </div>
      </div>
    </div>
  );
};

// =====================================================================
export const ReleaseEditor = ({ upcoming = false }) => {
  const { id } = useParams();
  const nav = useNavigate();
  const site = useSite();
  const isNew = id === "new";
  const [r, setR] = useState({ artist: "P. Frazy", type: "SINGLE", status: "draft", links: {}, tracklist: [], auto_publish: true });
  const [audioMedia, setAudioMedia] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [announcing, setAnnouncing] = useState(false);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [generatingArt, setGeneratingArt] = useState(false);
  const [artPrompt, setArtPrompt] = useState("");
  const set = (k, v) => setR((p) => ({ ...p, [k]: v }));

  const generateArtwork = async () => {
    if (isNew) return toast.error("Save the release first");
    if (!r.title) return toast.error("Give the release a title before generating art");
    setGeneratingArt(true);
    try {
      const { data } = await adminApi.post(`/admin/releases/${id}/generate-artwork`,
                                           { prompt: artPrompt || null }, { timeout: 120000 });
      set("artwork_url", data.url);
      toast.success("Cover generated");
      setArtPrompt("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGeneratingArt(false); }
  };

  const [blastKind, setBlastKind] = useState("release");
  const [testing, setTesting] = useState(null);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);

  const importLinks = async () => {
    setImporting(true);
    try {
      const { data } = await adminApi.post("/admin/links/import", { url: importUrl });
      const current = r.links || {};
      const merged = { ...current };
      let filled = 0;
      Object.entries(data.links).forEach(([k, v]) => { if (!current[k]) { merged[k] = v; filled += 1; } });
      set("links", merged);
      toast.success(filled ? `Filled ${filled} link${filled === 1 ? "" : "s"} (${Object.keys(data.links).join(", ")}). Review, then Save.` : `Found ${data.found} links but every field already had a value — nothing changed.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };
  const isUpcoming = upcoming || (r.release_at && new Date(r.release_at).getTime() > Date.now());
  const KIND_LABEL = { release: "Release-day blast", presave: "Pre-save reminder", lastcall: "Last-call reminder" };

  const sendTest = async (kind) => {
    setTesting(kind);
    try {
      const { data } = await adminApi.post(`/admin/releases/${id}/test-email`, { kind });
      toast.success(`Test ${KIND_LABEL[kind].toLowerCase()} email sent to ${data.to}. Check your inbox (and spam).`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(null); }
  };

  const openAudience = (kind = "release") => {
    if (isNew) return toast.error("Save the release first");
    setBlastKind(kind);
    setAudienceOpen(true);
  };

  const sendBlast = async (interests) => {
    setAnnouncing(true);
    try {
      const { data } = await adminApi.post(`/admin/releases/${id}/announce`, { interests, kind: blastKind });
      const audience = interests.length ? interests.join(", ") : "everyone";
      toast.success(`${KIND_LABEL[blastKind]} sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"} (${audience}).`);
      const stamp = new Date().toISOString();
      setR((p) => blastKind === "presave" ? { ...p, presave_announced_at: stamp }
        : blastKind === "lastcall" ? { ...p, lastcall_announced_at: stamp } : { ...p, announced: true });
      setAudienceOpen(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAnnouncing(false); }
  };

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/admin/releases/${id}`).then(async ({ data }) => {
      setR(data);
      if (data.audio_media_id) {
        try { const m = await adminApi.get(`/admin/media`); const found = m.data.find((x) => x.id === data.audio_media_id); if (found) setAudioMedia(found); } catch { /* */ }
      }
    }).catch(() => toast.error("Could not load")).finally(() => setLoading(false));
  }, [id, isNew]);

  const onAudio = (m) => { setAudioMedia(m); setR((p) => ({ ...p, audio_media_id: m.id, preview_url: m.url, peaks: m.peaks, full_peaks: m.full_peaks })); };
  const onArtwork = (url) => set("artwork_url", url);

  const save = async () => {
    if (!r.title) return toast.error("Title is required");
    setSaving(true);
    const body = { ...r };
    try {
      if (isNew) await adminApi.post("/admin/releases", body);
      else await adminApi.put(`/admin/releases/${id}`, body);
      site.refresh();
      toast.success(r.status === "published" ? "Saved. It's live." : "Saved.");
      nav(upcoming ? "/admin/upcoming" : "/admin/releases");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <p style={{ color: "var(--ash)" }}>Loading…</p>;
  const links = r.links || {};

  return (
    <div className="max-w-[760px]">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display foil-text" style={{ fontSize: "1.8rem" }}>{isNew ? (upcoming ? "New upcoming release" : "New release") : r.title}</h1>
        <div className="flex gap-3">
          <Btn variant="ghost" onClick={() => nav(-1)}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="save-release">{saving ? "Saving…" : "Save"}</Btn>
        </div>
      </div>

      {isUpcoming && !isNew && (
        <PreflightCard release={r} emailing={testing === "preflight"}
                       onEmail={async () => {
                         setTesting("preflight");
                         try { const { data } = await adminApi.post(`/admin/releases/${id}/preflight-reminder`); toast.success(`Pre-flight list emailed to ${data.to}`); }
                         catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                         finally { setTesting(null); }
                       }}
                       onFix={(section) => document.getElementById(`sec-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" })} />
      )}

      <Section title="Details" onSave={save} saving={saving} testid="save-details" id="sec-details">
        <Field label="Title"><Text value={r.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="f-title" /></Field>
        <Field label="Subtitle"><Text value={r.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} /></Field>
        <Field label="Artist" help="Defaults to P. Frazy. Change this when releasing music from another artist signed to Heart N Soul Records.">
          <Text value={r.artist || ""} onChange={(e) => set("artist", e.target.value)} placeholder="P. Frazy" data-testid="f-artist" />
        </Field>
        <Field label="Credit line" help="Shown under the title on the card. Leave empty for “P. Frazy · Heart N Soul Records”.">
          <Text value={r.credit || ""} onChange={(e) => set("credit", e.target.value)} placeholder="P. Frazy · Heart N Soul Records" data-testid="f-credit" />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Type"><Select value={r.type} onChange={(e) => set("type", e.target.value)}><option>SINGLE</option><option>ALBUM</option><option>EP</option></Select></Field>
          <Field label="Runtime"><Text value={r.runtime || ""} onChange={(e) => set("runtime", e.target.value)} placeholder="3:52" /></Field>
          <Field label="BPM"><Text type="number" value={r.bpm || ""} onChange={(e) => set("bpm", Number(e.target.value))} /></Field>
        </div>
        <div className="flex flex-wrap gap-6">
          <Toggle checked={!!r.featured} onChange={(v) => set("featured", v)} label="Featured (hero)" testid="f-featured" />
          {isUpcoming && <Toggle checked={r.feature_on_release !== false} onChange={(v) => set("feature_on_release", v)} label="Take the Featured badge on release day" testid="f-feature-on-release" />}
        </div>
        <p style={{ color: "var(--ash)", fontSize: ".72rem", marginTop: "-.5rem" }}>
          Only one release can be Featured — turning it on here removes it from the others automatically. Featured shows in the &ldquo;Out now&rdquo; section, so it belongs on the newest release that is already out.
        </p>
        <Field label="Blurb"><Area rows={2} value={r.blurb || ""} onChange={(e) => set("blurb", e.target.value)} /></Field>
        <Field label="Epigraph (optional)"><Text value={r.epigraph || ""} onChange={(e) => set("epigraph", e.target.value)} /></Field>
        <Field label="Lyrics (optional)" help={isUpcoming
          ? "Paste the full lyrics; line breaks are kept. Optional sing-along sync: start a line with a time counted from the beginning of the song, e.g. [0:04] In the quiet of the night — that line lights up as the 90-second preview plays, and across the whole song once it's out."
          : "Paste the full lyrics; line breaks are kept. Optional sing-along sync: start a line with a time counted from the beginning of the song, e.g. [0:04] In the quiet of the night — that line lights up as the full song plays."}>
          <Area rows={8} value={r.lyrics || ""} onChange={(e) => set("lyrics", e.target.value)} placeholder="Verse 1…" data-testid="f-lyrics" style={{ fontFamily: "inherit" }} />
        </Field>
        {(r.lyrics || "").trim() && <LyricsStamper lyrics={r.lyrics} previewUrl={r.preview_url} fullMediaId={!isUpcoming ? r.audio_media_id : null} onChange={(v) => set("lyrics", v)} />}
      </Section>

      <Section title="Schedule & status" onSave={save} saving={saving} testid="save-schedule" id="sec-schedule">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Status"><Select value={r.status} onChange={(e) => set("status", e.target.value)} data-testid="f-status"><option value="draft">Draft</option><option value="scheduled">Scheduled</option><option value="published">Published</option></Select></Field>
          <Field label="Release date & time" help="Site default timezone: America/Phoenix (no DST).">
            <Text type="datetime-local" value={toLocalInput(r.release_at)} onChange={(e) => set("release_at", fromLocalInput(e.target.value))} data-testid="f-release-at" />
          </Field>
        </div>
        {r.auto_published && <p className="font-caps text-gold" style={{ fontSize: ".55rem" }}>Auto-published on release date</p>}

        <div className="pt-4" style={{ borderTop: "var(--rule)" }}>
          <Toggle checked={r.announce_on_publish !== false} onChange={(v) => set("announce_on_publish", v)} label="Email my list on release day (turns the countdown into first-day streams)" testid="f-announce" />
          <div className="flex items-center gap-4 mt-3">
            <Btn variant="ghost" type="button" onClick={() => openAudience("release")} disabled={announcing || isNew} data-testid="announce-now">
              {announcing && blastKind === "release" ? "Sending…" : "Send release-day blast now"}
            </Btn>
            {r.announced && <span className="font-caps text-gold" style={{ fontSize: ".55rem" }}>Blast sent ✓</span>}
          </div>
          {isUpcoming && (
            <div className="flex items-center gap-4 mt-3">
              <Btn variant="ghost" type="button" onClick={() => openAudience("presave")} disabled={announcing || isNew || !r.presave} data-testid="presave-reminder-now">
                {announcing && blastKind === "presave" ? "Sending…" : "Send pre-save reminder now"}
              </Btn>
              {r.presave_announced_at
                ? <span className="font-caps text-gold" style={{ fontSize: ".55rem" }}>Reminder sent ✓</span>
                : !r.presave && <span className="font-caps text-ash" style={{ fontSize: ".55rem" }}>Add a Pre-save URL below first</span>}
            </div>
          )}
          {isUpcoming && (
            <div className="flex items-center gap-4 mt-3">
              <Btn variant="ghost" type="button" onClick={() => openAudience("lastcall")} disabled={announcing || isNew || !r.presave} data-testid="lastcall-reminder-now">
                {announcing && blastKind === "lastcall" ? "Sending…" : "Send last-call reminder now"}
              </Btn>
              {r.lastcall_announced_at
                ? <span className="font-caps text-gold" style={{ fontSize: ".55rem" }}>Last call sent ✓</span>
                : <span className="font-caps text-ash" style={{ fontSize: ".55rem" }}>"Drops tomorrow" email — send the day before release</span>}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-3" style={{ borderTop: "var(--rule)" }}>
            <span className="font-caps text-ash" style={{ fontSize: ".55rem" }}>Preview without emailing fans:</span>
            <Btn variant="ghost" type="button" onClick={() => sendTest("release")} disabled={testing || isNew} data-testid="test-email-release">
              {testing === "release" ? "Sending…" : "Email me the release-day test"}
            </Btn>
            {isUpcoming && (
              <>
                <Btn variant="ghost" type="button" onClick={() => sendTest("presave")} disabled={testing || isNew || !r.presave} data-testid="test-email-presave">
                  {testing === "presave" ? "Sending…" : "Email me the pre-save test"}
                </Btn>
                <Btn variant="ghost" type="button" onClick={() => sendTest("lastcall")} disabled={testing || isNew || !r.presave} data-testid="test-email-lastcall">
                  {testing === "lastcall" ? "Sending…" : "Email me the last-call test"}
                </Btn>
              </>
            )}
          </div>
          <p className="mt-2" style={{ color: "var(--ash)", fontSize: ".72rem" }}>Test emails go only to your owner login address, marked [TEST]. When this release goes live (on its date, or when you publish it), everyone on your mailing list gets a branded &ldquo;Out now&rdquo; email with a Listen button — automatically.</p>
        </div>

        {upcoming && (
          <>
            <Field label="Announcement blurb"><Area rows={2} value={r.announcement || ""} onChange={(e) => set("announcement", e.target.value)} /></Field>
            <div className="flex flex-wrap gap-6">
              <Toggle checked={!!r.artwork_pending} onChange={(v) => set("artwork_pending", v)} label="Artwork pending (show placeholder)" />
              <Toggle checked={r.auto_publish !== false} onChange={(v) => set("auto_publish", v)} label="Auto-publish on release date" testid="f-autopublish" />
              <Toggle checked={r.auto_presave_reminders !== false} onChange={(v) => set("auto_presave_reminders", v)} label="Auto-send pre-save reminder (7 days out) and last call (day before)" testid="f-auto-presave" />
            </div>
          </>
        )}
      </Section>

      <Section title="Artwork" onSave={save} saving={saving} testid="save-artwork" id="sec-artwork">
        <ImageUploader value={r.artwork_url} onChange={onArtwork} square />
        {!isNew && <ReadyToPost release={r} id={id} isUpcoming={isUpcoming} onCaptionSaved={(c) => set("post_caption", c)} />}
        {!isNew && (
          <div className="pt-3" style={{ borderTop: "var(--rule)" }}>
            <p className="mb-3" style={{ color: "var(--ash)", fontSize: ".72rem", lineHeight: 1.5 }}>
              No cover yet? Generate a candle-lit, gold-etched album cover from the release title and blurb.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <Text value={artPrompt} onChange={(e) => setArtPrompt(e.target.value)}
                    placeholder="Optional style direction (e.g. rose on marble, moody purple candlelight)"
                    data-testid="art-prompt" style={{ maxWidth: 380 }} />
              <Btn variant="ghost" type="button" onClick={generateArtwork} disabled={generatingArt}
                   data-testid="generate-artwork">
                {generatingArt ? "Painting cover…" : "✨ Generate cover"}
              </Btn>
            </div>
          </div>
        )}
      </Section>

      <Section title="Audio" onSave={save} saving={saving} testid="save-audio" id="sec-audio">
        {!isNew && (
          <p className="font-caps" style={{ color: isUpcoming ? "var(--ash)" : "var(--gold)", fontSize: ".58rem" }} data-testid="audio-mode">
            {isUpcoming ? "Fans hear: 90-second preview (full song unlocks automatically on release day)" : "Fans hear: full song, start to finish"}
          </p>
        )}
        <AudioUploader media={audioMedia} onChange={onAudio} />
      </Section>

      {(r.type === "ALBUM" || r.type === "EP") && (
        <Section title={`Tracklist ${r.tracklist?.length ? `(${r.tracklist.length})` : ""}`} onSave={save} saving={saving} testid="save-tracklist">
          <p style={{ color: "var(--ash)", fontSize: ".72rem", marginTop: "-.5rem" }}>
            Each track can have its own cover, 90-second audio preview, and optional music video (YouTube/Vimeo link, or upload).
          </p>
          {(r.tracklist || []).map((t, i) => (
            <TrackEditor
              key={i}
              track={t}
              index={i}
              total={r.tracklist.length}
              onChange={(next) => { const tl = [...r.tracklist]; tl[i] = { ...next, number: i + 1 }; set("tracklist", tl); }}
              onRemove={() => set("tracklist", r.tracklist.filter((_, j) => j !== i).map((tr, k) => ({ ...tr, number: k + 1 })))}
              onMove={(from, delta) => {
                const to = from + delta;
                if (to < 0 || to >= r.tracklist.length) return;
                const tl = [...r.tracklist];
                [tl[from], tl[to]] = [tl[to], tl[from]];
                set("tracklist", tl.map((tr, k) => ({ ...tr, number: k + 1 })));
              }}
            />
          ))}
          <Btn variant="ghost" onClick={() => set("tracklist", [...(r.tracklist || []), emptyTrack((r.tracklist || []).length)])} data-testid="add-track"><Plus size={13} /> Add track</Btn>
        </Section>
      )}

      <Section title="Links" onSave={save} saving={saving} testid="save-links" id="sec-links">
        <div className="p-4 mb-5 grid gap-3" style={{ border: "1px solid rgba(200,155,60,.3)", borderRadius: "var(--radius)" }} data-testid="link-import">
          <p className="font-caps text-gold" style={{ fontSize: ".58rem" }}>Import links from a landing page</p>
          <p style={{ color: "var(--ash)", fontSize: ".75rem" }}>Paste this release's DistroKid HyperFollow link (or any public page that lists the stores). We read the Spotify, Apple Music, Amazon and other store links off it and fill the empty fields below — nothing is saved until you click Save.</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Text value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="https://distrokid.com/hyperfollow/pfrazy/…" data-testid="link-import-url" className="flex-1" />
            <Btn type="button" variant="ghost" onClick={importLinks} disabled={importing || !importUrl} data-testid="link-import-btn">{importing ? "Reading…" : "Import links"}</Btn>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {Object.entries(LINK_META).map(([key, meta]) => (
            <Field key={key} label={meta.name}><Text value={links[key] || ""} onChange={(e) => set("links", { ...links, [key]: e.target.value })} placeholder="https://" /></Field>
          ))}
          <Field label="Pre-save URL"><Text value={r.presave || ""} onChange={(e) => set("presave", e.target.value)} placeholder="https://" /></Field>
          <Field label="Smart link URL"><Text value={r.smart_link || ""} onChange={(e) => set("smart_link", e.target.value)} placeholder="https://" /></Field>
        </div>
      </Section>

      <Section title="Music video" onSave={save} saving={saving} testid="save-video-section" id="sec-video">
        <MusicVideoBlock video={r.video} artworkUrl={r.artwork_url} onChange={(v) => set("video", v)} />
      </Section>

      {audienceOpen && (
        <AudiencePicker
          eyebrow={KIND_LABEL[blastKind]}
          releaseTitle={blastKind === "release" ? r.title : `${KIND_LABEL[blastKind]}: ${r.title}`}
          onCancel={() => setAudienceOpen(false)}
          onSend={sendBlast}
          sending={announcing}
        />
      )}
    </div>
  );
};

// =====================================================================
export const VideoEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const site = useSite();
  const isNew = id === "new";
  const [v, setV] = useState({ source_type: "youtube", status: "published", featured: false });
  const [fileMedia, setFileMedia] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/admin/videos/${id}`).then(({ data }) => setV(data)).catch(() => {}).finally(() => setLoading(false));
  }, [id, isNew]);

  const onFile = (m) => { setFileMedia(m); setV((p) => ({ ...p, file_url: m.url, poster_url: m.poster_url, duration: m.duration, video_media_id: m.id })); };

  const save = async () => {
    if (!v.title) return toast.error("Title is required");
    if (v.source_type !== "upload" && !v.url) return toast.error("Paste a video URL");
    if (v.source_type === "upload" && !v.file_url) return toast.error("Upload a video file");
    setSaving(true);
    try {
      if (isNew) await adminApi.post("/admin/videos", v);
      else await adminApi.put(`/admin/videos/${id}`, v);
      site.refresh();
      toast.success("Saved.");
      nav("/admin/videos");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <p style={{ color: "var(--ash)" }}>Loading…</p>;
  return (
    <div className="max-w-[680px]">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display foil-text" style={{ fontSize: "1.8rem" }}>{isNew ? "New video" : v.title}</h1>
        <div className="flex gap-3"><Btn variant="ghost" onClick={() => nav(-1)}>Cancel</Btn><Btn onClick={save} disabled={saving} data-testid="save-video">{saving ? "Saving…" : "Save"}</Btn></div>
      </div>
      <Section title="Video" onSave={save} saving={saving} testid="save-video-editor">
        <Field label="Title"><Text value={v.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="v-title" /></Field>
        <Field label="Caption"><Text value={v.caption || ""} onChange={(e) => set("caption", e.target.value)} /></Field>
        <Field label="Source"><Select value={v.source_type} onChange={(e) => set("source_type", e.target.value)} data-testid="v-source"><option value="youtube">YouTube URL</option><option value="vimeo">Vimeo URL</option><option value="upload">Uploaded file</option></Select></Field>
        {v.source_type !== "upload" ? (
          <Field label="Video URL" help="Paste the full YouTube or Vimeo link."><Text value={v.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://youtube.com/watch?v=…" data-testid="v-url" /></Field>
        ) : (
          <VideoUploader media={fileMedia || (v.file_url ? { url: v.file_url, poster_url: v.poster_url, duration: v.duration, original_filename: "uploaded video" } : null)} onChange={onFile} />
        )}
        <div className="flex flex-wrap gap-6">
          <Toggle checked={!!v.featured} onChange={(val) => set("featured", val)} label="Featured video" testid="v-featured" />
        </div>
        <Field label="Status"><Select value={v.status} onChange={(e) => set("status", e.target.value)}><option value="draft">Draft</option><option value="published">Published</option></Select></Field>
        <Field label="Linked release (optional)"><Select value={v.release_id || ""} onChange={(e) => set("release_id", e.target.value)}><option value="">— None —</option>{[...(site.music || []), ...(site.upcoming || [])].map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}</Select></Field>
      </Section>
    </div>
  );
};

// =====================================================================
// Category-driven size presets. Each variant has a `code` (stable ID),
// a `label` (customer-facing), and its own `price`.
const APPAREL_SIZES = [
  { code: "S", label: "S" },
  { code: "M", label: "M" },
  { code: "L", label: "L" },
  { code: "XL", label: "XL" },
  { code: "XXL", label: "XXL" },
  { code: "XXXL", label: "XXXL" },
];

export const SIZE_PRESETS = {
  Apparel: APPAREL_SIZES,
  "T-Shirt": APPAREL_SIZES,
  Hoodie: APPAREL_SIZES,
  Print: [
    { code: "12x12", label: '12×12" (30×30 cm)' },
    { code: "12x16", label: '12×16" (30×40 cm)' },
    { code: "12x18", label: '12×18" (30×45 cm)' },
    { code: "16x16", label: '16×16" (40×40 cm)' },
    { code: "18x24", label: '18×24" (45×60 cm)' },
    { code: "24x36", label: '24×36" (60×90 cm)' },
  ],
};

// Category-driven color presets — swatches for the admin & the customer merch card.
export const COLOR_PRESETS = {
  "T-Shirt": ["Baby Blue", "Black", "Dark Grey Heather", "Maize Yellow", "Navy", "Orange", "Silver", "White"],
  Hoodie: ["Black", "Forest Green", "Light Pink", "Navy", "Orchid", "Sand", "White"],
  "Tote Bag": ["Black", "Natural"],
  "Ceramic Mug": ["Ceramic Black", "Ceramic Blue", "Ceramic Green", "Ceramic Pink", "Ceramic Red", "Ceramic White", "Ceramic Yellow"],
};

export const COLOR_HEX = {
  "Black": "#0b0908",
  "White": "#f5f0e6",
  "Navy": "#1a2b4a",
  "Baby Blue": "#b8d4ea",
  "Dark Grey Heather": "#4a4d52",
  "Maize Yellow": "#f2c231",
  "Orange": "#f25c1a",
  "Silver": "#c9cbcc",
  "Forest Green": "#1f4d2e",
  "Orchid": "#b07cc6",
  "Light Pink": "#f4c2d0",
  "Sand": "#d8c4a0",
  "Natural": "#e8dcc4",
  "Ceramic Black": "#1a1a1a",
  "Ceramic Blue": "#1d2f8a",
  "Ceramic Green": "#b5d96a",
  "Ceramic Pink": "#f2b8c6",
  "Ceramic Red": "#d1202a",
  "Ceramic White": "#f7f5f0",
  "Ceramic Yellow": "#f2d24a",
  "Navy Blue": "#1a2b4a",
  "Charcoal Gray": "#3a3a3a",
  "Dark Brown": "#3d2b1f",
  "Beige": "#d9c9a3",
  "Cream": "#f0e6d0",
  "Off White": "#ece5d3",
  "Navy Blazer": "#1c2841",
  "Team Royal": "#1f47b0",
  "Team Gold": "#e8b923",
  "Charcoal Heather": "#4a4a4a",
  "Sport Grey": "#b9b9b9",
  "Dark Heather": "#3f4147",
  "Athletic Heather": "#c8c8c8",
  "Heather Navy": "#2b3550",
  "Red": "#c8102e",
  "Maroon": "#5b1a2a",
  "Steel Grey": "#8a9099",
  "River Blue Navy": "#243a5e",
  "Deep Smoke": "#5a5a5a",
};

// Colors the owner can tick for Printful / "Apparel (other)" items — swatches shown on the store card.
export const PRINTFUL_COLORS = ["Black", "White", "Navy Blazer", "Team Royal", "Team Gold", "Charcoal Heather",
  "Sport Grey", "Dark Heather", "Athletic Heather", "Heather Navy", "Red", "Maroon", "Forest Green",
  "Steel Grey", "River Blue Navy", "Deep Smoke", "Navy"];

export const MerchEditor = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const site = useSite();
  const isNew = id === "new";
  const [m, setM] = useState({ available: true, variants: [], category: "Apparel" });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setM((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/admin/merch/${id}`).then(({ data }) => {
      // Backwards compat: if the record has legacy `sizes: [string]`, hydrate variants
      // by copying the flat `price` into each. New records store `variants` directly.
      if (!data.variants && Array.isArray(data.sizes) && data.sizes.length) {
        data.variants = data.sizes.map((s) => ({ code: s, label: s, price: data.price || 0 }));
      }
      // Drop sizes that no longer exist in the category preset (e.g. retired Small/Medium/Large)
      const presetCodes = SIZE_PRESETS[data.category]?.map((o) => o.code);
      if (presetCodes && Array.isArray(data.variants)) {
        data.variants = data.variants.filter((v) => presetCodes.includes(v.code));
      }
      const palette = COLOR_PRESETS[data.category];
      if (palette && Array.isArray(data.colors)) {
        data.colors = data.colors.filter((c) => palette.includes(c));
      }
      setM({ variants: [], ...data });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id, isNew]);

  const preset = SIZE_PRESETS[m.category] || null;
  const colorPreset = COLOR_PRESETS[m.category] || null;
  const variants = m.variants || [];
  const getVariant = (code) => variants.find((v) => v.code === code);

  const toggleVariant = (opt) => {
    const exists = getVariant(opt.code);
    if (exists) {
      set("variants", variants.filter((v) => v.code !== opt.code));
    } else {
      set("variants", [...variants, { code: opt.code, label: opt.label, price: Number(m.price) || 0 }]);
    }
  };
  const setVariantPrice = (code, price) => {
    set("variants", variants.map((v) => (v.code === code ? { ...v, price: Number(price) || 0 } : v)));
  };

  // Whenever variants change, keep the top-level `price` in sync with the lowest variant price
  // so the merch card can show a "starting from" value.
  useEffect(() => {
    if (variants.length > 0) {
      const min = Math.min(...variants.map((v) => Number(v.price) || 0).filter((n) => n > 0));
      if (Number.isFinite(min) && min > 0 && min !== m.price) set("price", min);
    }
  }, [JSON.stringify(variants)]);

  const save = async () => {
    if (!m.name || !m.price) return toast.error("Name and price are required");
    if (variants.length > 0 && variants.some((v) => !v.price || Number(v.price) <= 0)) {
      return toast.error("Every selected size needs a price greater than 0");
    }
    setSaving(true);
    try {
      // Sync legacy `sizes` for older card views that still read that field
      const payload = { ...m, sizes: variants.map((v) => v.label) };
      if (isNew) await adminApi.post("/admin/merch", payload);
      else await adminApi.put(`/admin/merch/${id}`, payload);
      site.refresh();
      toast.success("Saved.");
      nav("/admin/merch");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <p style={{ color: "var(--ash)" }}>Loading…</p>;
  return (
    <div className="max-w-[680px]">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display foil-text" style={{ fontSize: "1.8rem" }}>{isNew ? "New product" : m.name}</h1>
        <div className="flex gap-3"><Btn variant="ghost" onClick={() => nav(-1)}>Cancel</Btn><Btn onClick={save} disabled={saving} data-testid="save-merch">{saving ? "Saving…" : "Save"}</Btn></div>
      </div>
      <Section title="Product" onSave={save} saving={saving} testid="save-product">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><Text value={m.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="m-name" /></Field>
          <Field label="SKU"><Text value={m.sku || ""} onChange={(e) => set("sku", e.target.value)} /></Field>
          <Field
            label={variants.length > 0 ? "Base price (auto = lowest size)" : "Price (USD)"}
            help={variants.length > 0 ? "This is auto-filled from your lowest-priced size below. Customers see it as \"Starting at\"." : undefined}
          >
            <Text type="number" value={m.price || ""} onChange={(e) => set("price", Number(e.target.value))} disabled={variants.length > 0} data-testid="m-price" />
          </Field>
          <Field label="Category">
            <Select value={m.category || "T-Shirt"} onChange={(e) => {
              const cat = e.target.value;
              const codes = SIZE_PRESETS[cat]?.map((o) => o.code);
              const palette = COLOR_PRESETS[cat];
              setM((p) => ({
                ...p, category: cat,
                variants: codes ? (p.variants || []).filter((v) => codes.includes(v.code)) : [],
                colors: palette ? [...palette] : [],
              }));
            }} data-testid="m-category">
              <option value="T-Shirt">T-Shirt</option>
              <option value="Hoodie">Hoodie</option>
              <option value="Apparel">Apparel (other)</option>
              <option value="Print">Print</option>
              <option value="Ceramic Mug">Ceramic Mug</option>
              <option value="Tote Bag">Tote Bag</option>
              <option value="Accessories">Accessories</option>
              <option value="Music">Music</option>
              <option value="Sticker">Sticker</option>
              <option value="Other">Other</option>
            </Select>
          </Field>
        </div>

        {preset ? (
          <Field
            label={m.category === "Print" ? "Print sizes & prices" : "Sizes & prices"}
            help="Click a size to include it, then set its price. Leave all sizes unselected to sell as a single item."
          >
            <div className="flex flex-col gap-2" data-testid="m-variants">
              {preset.map((opt) => {
                const v = getVariant(opt.code);
                const active = Boolean(v);
                return (
                  <div key={opt.code} className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleVariant(opt)}
                      className="font-caps"
                      style={{
                        fontSize: ".68rem",
                        padding: ".45rem .9rem",
                        borderRadius: "var(--radius)",
                        border: `1px solid ${active ? "var(--gold)" : "rgba(200,155,60,.3)"}`,
                        color: active ? "var(--ink)" : "var(--parchment)",
                        background: active ? "var(--gold)" : "transparent",
                        cursor: "pointer",
                        minWidth: m.category === "Print" ? 180 : 96,
                        textAlign: "left",
                      }}
                      data-testid={`m-variant-${opt.code}`}
                    >
                      {opt.label}
                    </button>
                    {active && (
                      <>
                        <span className="font-caps text-ash" style={{ fontSize: ".65rem" }}>$</span>
                        <div style={{ maxWidth: 120 }}>
                          <Text
                            type="number"
                            value={v.price || ""}
                            onChange={(e) => setVariantPrice(opt.code, e.target.value)}
                            placeholder="0"
                            data-testid={`m-variant-price-${opt.code}`}
                          />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              {variants.length > 0 && (
                <button
                  type="button"
                  onClick={() => set("variants", [])}
                  className="font-caps self-start mt-1"
                  style={{ fontSize: ".62rem", color: "var(--rose)", background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
                  data-testid="m-variants-clear"
                >
                  Clear all sizes
                </button>
              )}
            </div>
          </Field>
        ) : null}

        {colorPreset ? (
          <Field
            label="Colors shown on the store"
            help={`Every ${m.category} automatically offers these ${colorPreset.length} colors on the merch page — they mirror your DistroKid listing. Shoppers pick their color on DistroKid at checkout.`}
          >
            <div className="flex flex-wrap gap-2" data-testid="m-colors">
              {colorPreset.map((c) => (
                <span
                  key={c}
                  className="font-caps"
                  style={{
                    fontSize: ".65rem",
                    padding: ".4rem .7rem",
                    borderRadius: "var(--radius)",
                    border: "1px solid rgba(200,155,60,.3)",
                    color: "var(--parchment)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: ".5rem",
                  }}
                  data-testid={`m-color-${c.replace(/\s+/g, "-")}`}
                >
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: COLOR_HEX[c] || "#888",
                      border: "1px solid rgba(255,255,255,.25)",
                      boxShadow: "inset 0 0 0 1px rgba(0,0,0,.3)",
                    }}
                  />
                  <span>{c}</span>
                </span>
              ))}
            </div>
          </Field>
        ) : (
          <Field
            label="Colors shown on the store (optional)"
            help="Tick the colors this item comes in on Printful. They appear as swatches on the merch card so fans see their options before they click Buy. Leave all unticked to show none."
          >
            <div className="flex flex-wrap gap-2" data-testid="m-colors">
              {PRINTFUL_COLORS.map((c) => {
                const on = (m.colors || []).includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => set("colors", on ? (m.colors || []).filter((x) => x !== c) : [...(m.colors || []), c])}
                    className="font-caps"
                    style={{
                      fontSize: ".65rem",
                      padding: ".4rem .7rem",
                      borderRadius: "var(--radius)",
                      border: `1px solid ${on ? "var(--gold)" : "rgba(200,155,60,.3)"}`,
                      color: on ? "var(--ink)" : "var(--parchment)",
                      background: on ? "var(--gold)" : "transparent",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: ".5rem",
                    }}
                    data-testid={`m-color-${c.replace(/\s+/g, "-")}`}
                  >
                    <span aria-hidden style={{ display: "inline-block", width: 14, height: 14, borderRadius: "50%", background: COLOR_HEX[c] || "#888", border: "1px solid rgba(255,255,255,.25)", boxShadow: "inset 0 0 0 1px rgba(0,0,0,.3)" }} />
                    <span>{c}</span>
                  </button>
                );
              })}
            </div>
          </Field>
        )}
        <Field label="Description"><Area rows={2} value={m.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>
        <Field label="Fit note (optional)" help="Short sizing tip shown under the size buttons, e.g. &quot;Runs small — order one size up&quot;.">
          <Text value={m.fit_note || ""} onChange={(e) => set("fit_note", e.target.value)} placeholder="Runs small — order one size up" data-testid="m-fit-note" />
        </Field>
        <Field label="Fulfillment URL (optional)" help="If set, the &quot;Buy Now&quot; button opens this URL in a new tab (e.g. your DistroKid Merch product page) instead of using Stripe checkout. Leave blank to use built-in Stripe.">
          <Text value={m.fulfillment_url || ""} onChange={(e) => set("fulfillment_url", e.target.value)} placeholder="https://distrokid.com/merch/..." data-testid="m-fulfillment-url" />
        </Field>
        <ImageUploader value={m.image_url} onChange={(url) => set("image_url", url)} square label="Product photo" />
        <Toggle checked={m.available !== false} onChange={(v) => set("available", v)} label="Available (shown on site)" />
        <Toggle checked={Boolean(m.fan_favorite)} onChange={(v) => set("fan_favorite", v)} label="Fan Favorite — gold ribbon on the card and shown first in the store" testid="m-fan-favorite" />
      </Section>
    </div>
  );
};

// =====================================================================
export const MediaLibrary = () => {
  const [items, setItems] = useState([]);
  const load = () => adminApi.get("/admin/media").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete this media?")) return; await adminApi.delete(`/admin/media/${id}`); load(); };
  return (
    <div>
      <h1 className="font-display foil-text mb-6" style={{ fontSize: "1.8rem" }}>Media library</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((m) => (
          <div key={m.id} className="p-3" style={panel} data-testid={`media-${m.id}`}>
            <div className="aspect-square grid place-items-center overflow-hidden mb-2" style={{ background: "#000" }}>
              {m.kind === "image" && <img src={mediaUrl(m.url)} alt={m.alt} className="w-full h-full object-cover" />}
              {m.kind === "audio" && <span className="font-caps text-gold" style={{ fontSize: ".6rem" }}>AUDIO</span>}
              {m.kind === "video" && (m.poster_url ? <img src={mediaUrl(m.poster_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-caps text-gold" style={{ fontSize: ".6rem" }}>VIDEO</span>)}
            </div>
            <p className="truncate" style={{ color: "var(--parchment)", fontSize: ".72rem" }}>{m.original_filename}</p>
            <div className="flex items-center justify-between mt-1">
              <span style={{ color: "var(--ash)", fontSize: ".62rem" }}>{m.kind} · {(m.size / 1024 / 1024).toFixed(1)}MB</span>
              <button onClick={() => del(m.id)} className="text-rose" aria-label="Delete"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p style={{ color: "var(--ash)" }}>No media yet. Uploads from any editor appear here.</p>}
      </div>
    </div>
  );
};

export { RotateCcw };
